import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Spin } from 'antd'
import { ACCOUNT_PAGE, HOME_PAGE } from '../../../configuration/paths'
import { getTrafficMetrics, getTrafficUtilization, getUnitNames, setTrafficMetrics } from '../../../store'
import { MetricChart } from './MetricChart'
import { UnitMap } from './UnitMap'
import { UtilizationTimeline } from './UtilizationTimeline'
import { exportCsv, exportPdf, exportPptx } from './exports'
import { DateFilter } from '../../common/DateFilter'
import { ExportButtons } from '../../common/ExportButtons'
import { UnitsDrawer } from '../../common/UnitsDrawer'
import './styles.scss'

const INTERVALS = ['Past 24 Hours', 'Past Week', 'Past Month', 'Custom']

// Local YYYY-MM-DD (NOT toISOString, which is UTC and rolls the date forward in
// evening US timezones — that made "Yesterday" resolve to today).
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d }

export const TrafficMetrics = () => {
  const loggedIn = !!useSelector(s => s.app.auth.token)
  const role = useSelector(s => s.app.profile.role)
  const profile = useSelector(s => s.app.profile)
  const nav = useNavigate(); const dispatch = useDispatch()

  // Persisted across navigation (issue #5): restore filters + loaded data on return.
  const persisted = useSelector(s => s.app.trafficMetrics)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [interval, setIntervalSel] = useState('Past Week')   // inline date filter (matches Alerts UX)
  const [tab, setTab] = useState('trends')
  const [unitOptions, setUnitOptions] = useState(persisted.unitOptions?.length ? persisted.unitOptions : [])   // [{serial, name}]
  const [filter, setFilter] = useState(persisted.filter || { units: [], start: iso(daysAgo(7)), end: iso(daysAgo(0)), bucket: 'day' })
  const [data, setData] = useState(persisted.data || null)
  const [loading, setLoading] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [exportStatus, setExportStatus] = useState(null)
  const [utilData, setUtilData] = useState(persisted.utilData || null)
  const [utilLoading, setUtilLoading] = useState(false)

  // True when the current filter already has cached results in the store (just restored) —
  // used to skip the auto-refetch on mount so navigating back is instant.
  const cachedForFilter = persisted.filter && JSON.stringify(persisted.filter) === JSON.stringify(filter)

  useEffect(() => {
    if (!loggedIn) nav(HOME_PAGE); else if (role === 'user') nav(ACCOUNT_PAGE)
  }, [loggedIn, role, nav])

  useEffect(() => {
    if (unitOptions.length) return   // restored from the store; don't refetch the unit list
    dispatch(getUnitNames()).then(r => {
      // rows are [name, serial] pairs; de-dup by serial (stale data can have two
      // names for one serial, which otherwise produces duplicate rows/selections).
      const seen = new Set(); const opts = []
      for (const p of (r.payload || [])) {
        if (!seen.has(p[1])) { seen.add(p[1]); opts.push({ name: p[0], serial: p[1] }) }
      }
      setUnitOptions(opts)
      dispatch(setTrafficMetrics({ unitOptions: opts }))
    })
  }, [dispatch])   // eslint-disable-line react-hooks/exhaustive-deps -- fetch-once guard

  useEffect(() => {
    if (!filter.units.length) return
    if (cachedForFilter && persisted.data) return   // restored from store — instant, no refetch
    setLoading(true)
    dispatch(getTrafficMetrics(filter)).then(r => {
      setData(r.payload); setLoading(false)
      dispatch(setTrafficMetrics({ filter, data: r.payload }))
    })
  }, [dispatch, filter])   // eslint-disable-line react-hooks/exhaustive-deps

  // Utilization fetched eagerly (light projection-only query) so it's ready for both
  // the tab and the PDF/PPTX report. Uptime is independent of traffic detections.
  useEffect(() => {
    if (!filter.units.length) { setUtilData(null); return }
    if (cachedForFilter && persisted.utilData) return   // restored from store
    setUtilLoading(true)
    dispatch(getTrafficUtilization(filter)).then(r => {
      setUtilData(r.payload); setUtilLoading(false)
      dispatch(setTrafficMetrics({ utilData: r.payload }))
    })
  }, [dispatch, filter])   // eslint-disable-line react-hooks/exhaustive-deps

  // Inline interval/date filter (unified with Alerts) → maps to start/end/bucket.
  const applyInterval = (preset) => {
    setIntervalSel(preset)
    if (preset === 'Custom') return   // dates arrive via onCustomRange
    const end = iso(daysAgo(0))
    if (preset === 'Past 24 Hours') setFilter(f => ({ ...f, start: iso(daysAgo(1)), end, bucket: 'hour' }))
    else if (preset === 'Past Week') setFilter(f => ({ ...f, start: iso(daysAgo(7)), end, bucket: 'day' }))
    else if (preset === 'Past Month') setFilter(f => ({ ...f, start: iso(daysAgo(30)), end, bucket: 'day' }))
  }
  const onCustomRange = (from, to) => setFilter(f => ({ ...f, start: from, end: to, bucket: 'day' }))

  const runExport = (label, fn) => {
    setExportError(null)
    setExportStatus(label)
    Promise.resolve(fn())
      .then(() => setExportStatus(null))
      .catch(e => { setExportStatus(null); setExportError(e?.message || 'Export failed') })
  }

  const t = data?.tiles || {}
  const reportMeta = {
    company: profile.company_name || profile.group_name || '',
    user: profile.username || profile.name || 'unknown',
    date: new Date().toLocaleString(),
  }
  return (
    <div className="tm-page">
      <DateFilter
        options={INTERVALS} value={interval} onChange={applyInterval} onCustomRange={onCustomRange}
        rightSlot={
          <>
            <button className="rz-units-btn" onClick={() => setDrawerOpen(true)}>
              ⚙ Units · {filter.units.length}
            </button>
            <ExportButtons
              disabled={!data || !!exportStatus}
              onCsv={() => runExport('Generating CSV… (large ranges can take a minute)', () => exportCsv(filter))}
              onPdf={() => runExport('Building PDF report…', () => exportPdf(data, filter, reportMeta))}
              onPptx={() => runExport('Building PPTX report…', () => exportPptx(data, filter, reportMeta))}
            />
          </>
        }
      />

      {data?.partial && <div className="tm-banner">Some units could not be loaded: {data.failed_units.join(', ')}</div>}
      {exportStatus && <div className="tm-banner tm-status"><span className="tm-spinner" />{exportStatus}</div>}
      {exportError && <div className="tm-banner">Export failed: {exportError}</div>}

      <div className="tm-tiles">
        <div className="tm-tile"><b>{t.track_count ?? '—'}</b><span>vehicles</span></div>
        <div className="tm-tile"><b>{t.avg_passing_speed_mph ?? '—'}</b><span>mph avg</span></div>
        <div className="tm-tile"><b>{t.avg_merging_distance_ft ?? '—'}</b><span>ft merge</span></div>
      </div>

      <div className="tm-tabs">
        {['trends', 'distributions', 'map', 'utilization'].map(k =>
          <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{k}</button>)}
      </div>

      {!filter.units.length ? <p className="tm-empty">Select units (⚙ Units) to begin.</p> :
        tab === 'utilization' ? (
          utilLoading ? <div className="tm-empty"><Spin /></div> : <UtilizationTimeline data={utilData} />
        ) :
        loading ? <div className="tm-empty"><Spin /></div> :
        !data ? <p className="tm-empty">No data for this range.</p> :
        // Map = GPS coverage, independent of traffic. Show it even when track_count=0
        // (a unit can be online/armed/moving with no passing-car detections); UnitMap
        // has its own "no GPS positions" empty state.
        tab === 'map' ? <div className="tm-body" id="tm-capture"><UnitMap tracks={data.unit_tracks} /></div> :
        !data.tiles.track_count ? <p className="tm-empty">No traffic data for this range.</p> :
        <div className="tm-body" id="tm-capture">
          {tab === 'trends' && <>
            <MetricChart title="Passing speed (avg mph)" data={data.series.passing_speed_mph} xKey="t" yKey="avg" unit=" mph" yLabel="mph" xLabel="Date" />
            <MetricChart title={`Traffic volume (vehicles / ${data.bucket === 'hour' ? 'hour' : 'day'})`} data={data.series.volume} xKey="t" yKey="count" unit={` vehicles / ${data.bucket === 'hour' ? 'hour' : 'day'}`} yLabel={`vehicles / ${data.bucket === 'hour' ? 'hour' : 'day'}`} xLabel="Date" kind="bar" />
          </>}
          {tab === 'distributions' && <>
            <MetricChart title="Passing speed distribution" data={data.histograms.passing_speed_mph} xKey="bin" yKey="count" unit=" cars" yLabel="count" xLabel="mph" kind="bar" />
            <MetricChart title="Merge distance distribution" data={data.histograms.merging_distance_ft} xKey="bin" yKey="count" unit=" cars" yLabel="count" xLabel="ft" kind="bar" />
          </>}
        </div>}

      {/* Off-screen: always renders ALL charts + map so exports include every plot
          regardless of the active tab. Tiles/charts are loaded by export time. */}
      {data ? (
        <div id="tm-export-charts" aria-hidden="true"
          style={{ position: 'absolute', left: '-10000px', top: 0, width: '760px' }}>
          <MetricChart title="Passing speed (avg mph)" data={data.series.passing_speed_mph} xKey="t" yKey="avg" unit=" mph" yLabel="mph" xLabel="Date" />
          <MetricChart title={`Traffic volume (vehicles / ${data.bucket === 'hour' ? 'hour' : 'day'})`} data={data.series.volume} xKey="t" yKey="count" unit={` vehicles / ${data.bucket === 'hour' ? 'hour' : 'day'}`} yLabel={`vehicles / ${data.bucket === 'hour' ? 'hour' : 'day'}`} xLabel="Date" kind="bar" />
          <MetricChart title="Passing speed distribution" data={data.histograms.passing_speed_mph} xKey="bin" yKey="count" unit=" cars" yLabel="count" xLabel="mph" kind="bar" />
          <MetricChart title="Merge distance distribution" data={data.histograms.merging_distance_ft} xKey="bin" yKey="count" unit=" cars" yLabel="count" xLabel="ft" kind="bar" />
          <UnitMap tracks={data.unit_tracks} />
          {utilData && <div className="tm-chart-title" style={{ marginTop: '8px' }}>Utilization (uptime / armed)</div>}
          {utilData && <UtilizationTimeline data={utilData} />}
        </div>
      ) : null}

      <UnitsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
        units={unitOptions} value={filter} onApply={(v) => setFilter(f => ({ ...f, units: v.units }))} />
    </div>
  )
}
